import React, {useState, useEffect} from "react";
import {useSearchParams} from "react-router-dom";
import {
  Card,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Select,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  Typography
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET} from "APIclient";
import properties from "constants/ApplicationConstants";

function TabPanel(props) {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <MDBox p={3}>{children}</MDBox>}
    </div>
  );
}

const RATING_LEGEND = [
  {points: 1, rating: "Unsatisfactory", comment: "Performance is consistently non acceptable"},
  {
    points: 2,
    rating: "Below Expectations",
    comment: "Performance fails to meet job requirement on frequent basis"
  },
  {
    points: 3,
    rating: "Meets Expectations",
    comment: "Performance is regularly competent & dependable"
  },
  {
    points: 4,
    rating: "Exceeds Expectations",
    comment: "Performance is routinely above expectation"
  },
  {points: 5, rating: "Outstanding", comment: "Performance is consistently superior"}
];

const AppraisalForm = () => {
  const [searchParams] = useSearchParams();
  const empId = (searchParams.get("empId") || "").trim();
  const empName = searchParams.get("name");
  const employeeName = empName ? decodeURIComponent(empName) : "Employee";

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [data, setData] = useState({
    employeeAccessSkills: [],
    technicalSecondary: [],
    competencyPrimary: [],
    competencySecondary: [],
    questionnaire: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await GET(`admin/appraisal/form-overview?empId=${empId}`);
        const result = response?.data?.data || response.data;

        setData({
          employeeAccessSkills: (result.employeeAccessSkillSets || []).map(item => ({
            skillName: item.skillSet
          })),
          technicalSecondary: (result.technicalSecondary || []).map(item => ({
            skillName: item.skillSet
          })),
          competencyPrimary: (result.competencyPrimary || []).map(item => ({
            skillName: item.skillSet
          })),
          competencySecondary: (result.competencySecondary || []).map(item => ({
            skillName: item.skillSet
          })),
          questionnaire: result.questionnaire || []
        });
      } catch (err) {
        setError("Failed to load form data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [empId]);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const renderSkillsTable = (skills = [], showAppraiser = true) => {
    if (skills.length === 0) {
      return (
        <Typography color="text" mt={3} fontSize={16} fontFamily={properties.fontFamily}>
          No skills assigned.
        </Typography>
      );
    }

    return (
      <TableContainer sx={{mt: 2}}>
        <Table>
          <TableHead sx={{display: "table-header-group"}}>
            <TableRow>
              <TableCell width="5%" align="left">
                #
              </TableCell>
              <TableCell width="30%" align="left">
                Skill / Competency
              </TableCell>
              <TableCell width="50%" align="center">
                Self Score
              </TableCell>
              {showAppraiser && (
                <TableCell width="15%" align="center">
                  Appraiser Rating
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {skills.map((skill, i) => (
              <TableRow key={i}>
                <TableCell align="left">{i + 1}</TableCell>
                <TableCell align="left">{skill.skillName}</TableCell>
                <TableCell align="center">
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <FormControl fullWidth size="small">
                        <Select defaultValue="" displayEmpty sx={{height: 38}}>
                          <MenuItem value="" disabled>
                            <em>Rate</em>
                          </MenuItem>
                          {[1, 2, 3, 4, 5].map(n => (
                            <MenuItem key={n} value={n}>
                              {n}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={9}>
                      <TextField fullWidth size="small" placeholder="Comments" variant="outlined" />
                    </Grid>
                  </Grid>
                </TableCell>
                {showAppraiser && (
                  <TableCell align="center">
                    <FormControl size="small" sx={{minWidth: 80}}>
                      <Select defaultValue="" displayEmpty sx={{height: 38}}>
                        <MenuItem value="" disabled>
                          <em>Rate</em>
                        </MenuItem>
                        {[1, 2, 3, 4, 5].map(n => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress color="info" />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={4}>
          <Alert severity="error">{error}</Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  const removeLeadingNumber = (text = "") => {
    return text.replace(/^\d+\.\s*/, "");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={1}>
        <Card>
          <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="600" fontFamily={properties.fontFamily}>
              KRA Form Overview
            </Typography>
            {empId && (
              <Typography variant="h6" fontFamily={properties.fontFamily} fontWeight={600}>
                {employeeName} (EMP ID: {empId})
              </Typography>
            )}
          </MDBox>
          {!empId && (
            <MDBox p={2}>
              <Typography variant="h6" fontFamily={properties.fontFamily}>
                Performance Appraisal Form (Ratings)
              </Typography>
              <MDTypography variant="button" color="text" fontWeight="regular">
                The following ratings must be used to ensure commonality of language and consistency
                on overall ratings.
              </MDTypography>
              <MDBox mt={2}>
                <TableContainer component={Paper} elevation={0} sx={{border: "1px solid #e0e0e0"}}>
                  <Table size="small">
                    <TableHead sx={{display: "table-header-group"}}>
                      <TableRow>
                        <TableCell component="th" width="10%" align="left">
                          <strong>Points</strong>
                        </TableCell>
                        <TableCell component="th" width="30%" align="left">
                          <strong>Ratings</strong>
                        </TableCell>
                        <TableCell component="th" align="left">
                          <strong>Comments Justifying Ratings</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {RATING_LEGEND.map(row => (
                        <TableRow key={row.points}>
                          <TableCell align="left">{row.points}</TableCell>
                          <TableCell align="left">{row.rating}</TableCell>
                          <TableCell align="left">{row.comment}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            </MDBox>
          )}
          <MDBox sx={{borderBottom: 1, borderColor: "divider", px: 2, pt: 1}}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="appraisal tabs">
              <Tab label="My Goals / Technical Primary" />
              <Tab label="Technical Secondary" />
              <Tab label="Competency Primary" />
              <Tab label="Competency Secondary" />
              <Tab label="Questionnaire" />
            </Tabs>
          </MDBox>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" fontFamily={properties.fontFamily}>
              Technical Primary
            </Typography>
            {renderSkillsTable(data.employeeAccessSkills)}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" fontFamily={properties.fontFamily}>
              Technical Secondary
            </Typography>
            {renderSkillsTable(data.technicalSecondary)}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" fontFamily={properties.fontFamily}>
              Competency Primary
            </Typography>
            {renderSkillsTable(data.competencyPrimary)}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" fontFamily={properties.fontFamily}>
              Competency Secondary
            </Typography>
            {renderSkillsTable(data.competencySecondary)}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Typography variant="h6" fontFamily={properties.fontFamily}>
              Questionnaire
            </Typography>
            {data.questionnaire.length === 0 ? (
              <MDTypography color="text">No questions assigned.</MDTypography>
            ) : (
              <MDBox mt={2}>
                {data.questionnaire.map((q, i) => (
                  <MDBox key={i} mb={4}>
                    <MDTypography variant="body2" fontWeight="regular" mb={1} color="text">
                      {i + 1}. {removeLeadingNumber(q.skillSet)}
                    </MDTypography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      placeholder="Type your answer here..."
                    />
                  </MDBox>
                ))}
              </MDBox>
            )}
          </TabPanel>
          {/* 
          <MDBox p={3} textAlign="right" borderTop="1px solid #eee">
            <MDButton variant="gradient" color="info" size="large">
              Save & Submit Appraisal
            </MDButton>
          </MDBox> */}
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default AppraisalForm;
